/**
 * The marks around a sentence, by Unicode category rather than a hand-kept
 * list, so every kind of quote is covered at once — «» „“ ‚‘ “” ‹› 「」 () []
 * (Codex on #1260, owner's decision 01.10). One table for every rule below.
 *
 * Closing: `Pe` (brackets, 」), `Pf` (» ” ’ ›) and `Pi` too — “ and ‘ close
 * „…“ and ‚…‘. Opening: `Ps` („ ‚ ( [ 「) and `Pi` (« “ ‘ ‹), plus Spanish
 * ¿ and ¡. The straight " and ' are both: a leading run opens, a trailing run
 * closes — position decides. Markdown * and _ wrap either side.
 */
const QUOTES_CLOSE = String.raw`\p{Pe}\p{Pf}\p{Pi}"'`;
const QUOTES_OPEN = String.raw`\p{Ps}\p{Pi}"'¿¡`;
const EMPHASIS = '*_';
/**
 * A bracket after the stop is an aside, not the sentence's end: «(двічі.)»,
 * «（二次。）», «〔…〕», «【…】» — every closing bracket (`Pe`) that is not a
 * quotation mark by Unicode (`Quotation_Mark`: «」』｣〞〟﹂﹄», Codex on #1447),
 * nor a CJK title mark «〉》», which closes a title as a quote does (#1423).
 */
const ASIDE = String.raw`(?![\p{Quotation_Mark}〉》])\p{Pe}`;

/**
 * What ends a sentence: every sentence terminal of any script (`.!?`, the CJK
 * «。！？», «‼», …), the ellipsis, and «:» / «;» in their ASCII and full-width
 * forms, which lead into what follows (#1423) — by property, not a hand list.
 */
const SENTENCE_END = String.raw`\p{Sentence_Terminal}…:;：；`;
/** The same without «:» and «;» — what closes a sentence for good. */
const SENTENCE_CLOSE = String.raw`\p{Sentence_Terminal}…`;

/** Any run of closing marks after a stop. */
const CLOSING_RUN = `[${QUOTES_CLOSE}${EMPHASIS}]*`;
/** The same run when a bracket in it means the sentence goes on. */
const CLOSING_RUN_NO_ASIDE = `(?:(?!${ASIDE})[${QUOTES_CLOSE}${EMPHASIS}])*`;

/**
 * Ends a sentence that the next one follows — once.
 *
 * A recommendation is shown as its lead («what the agent did») followed by
 * the advice. The model writes the lead as a sentence, usually already ended
 * with «.», «?» or «!», and a stop appended after it printed
 * «…про майбутню потребу.. Визнати…» in every recommendation — in the
 * analysis panel, the PDF and the CRM report alike (#1110).
 *
 * A closing quote of any kind after the stop still counts as ended («…сказала
 * «Дайте спокій».»; „…“, ‹…›, 「…」 too), and so does markdown emphasis the
 * model wraps around it («**бюджет.**»). A closing bracket does not:
 * «(двічі.)» or «(!)» is an aside inside the sentence, which still needs its
 * own stop. «:» and «;»
 * already lead into what follows. A trailing comma or dash is a sentence cut
 * short, and the stop replaces it rather than following it («перебив,.»).
 */
const ENDED = new RegExp(`[${SENTENCE_END}]${CLOSING_RUN_NO_ASIDE}$`, 'u');
const TRAILING_SPACE = /[\s​﻿]+$/u;
const TRAILING_CONTINUATION = /[\s,，、\p{Pd}]+$/u;

export function endSentence(text: string): string {
  const trimmed = text.replace(TRAILING_SPACE, '');
  if (trimmed === '' || ENDED.test(trimmed)) return trimmed;
  // Checked again once the dangling comma or dash is gone: «перебив. —» was
  // already ended, and a stop after it printed «перебив..» (Codex on #1260).
  const cut = trimmed.replace(TRAILING_CONTINUATION, '');
  if (cut === '' || ENDED.test(cut)) return cut;
  return `${cut}.`;
}

/**
 * A single final «.», alone or inside whatever closes the sentence after it:
 * a quote of any kind, a bracket («(двічі.)»), markdown emphasis
 * («**привітався.**») or any run of them («**«Вітаю.»**») — the stop goes, the
 * closing marks stay (Codex on #1260).
 */
const FINAL_STOP = new RegExp(`([^.])\\.(${CLOSING_RUN})$`, 'u');

/**
 * The same sentence without its final stop, for when a dash continues it:
 * «Агент привітався — клієнт відкрився», not «привітався. — клієнт…». Only a
 * single «.» goes; «?», «!» and an ellipsis are part of what was said.
 */
export function withoutFinalStop(text: string): string {
  return text.replace(TRAILING_SPACE, '').replace(FINAL_STOP, '$1$2');
}

/**
 * A first word made only of lowercase letters — an apostrophe or a hyphen
 * inside it included («з'ясувати», «під’єднати», «зʼясувати», «по-перше») —
 * ending at a space, a comma-like stop, a closing quote or bracket, a stop or
 * a colon before a space or the end («запитати: …», «запитайте.»), an
 * ellipsis typed as three dots («запитати...», #1423) or the end itself — an
 * ordinary word.
 * `iPhone`, `eBay`, `https://…`, `mailto:…` and `www.…` do not match: their
 * casing is the name itself, and a stop or colon glued to the next character
 * is part of an address, not the end of a word (Codex on #1260).
 */
const PLAIN_FIRST_WORD = new RegExp(
  String.raw`^\p{Ll}+(?:['’ʼ-]\p{Ll}+)*` +
    // Emphasis ends the word only when no letter or digit follows it: in
    // «utm_source» the «_» is part of the name.
    String.raw`(?=[\s,;!?…${QUOTES_CLOSE}]|[${EMPHASIS}]+(?![\p{L}\p{N}])|\.{3}|[.:](?=\s|$)|$)`,
  'u'
);

/**
 * Marks that open a sentence before its first word: Spanish «¿» and «¡», an
 * opening quote or bracket, markdown emphasis («¿preguntar…?», ««запитати» про
 * бюджет», «**запитати**»), and any whitespace the model left before or
 * between them («  запитати»). The capital goes to the word after them; the
 * marks and the whitespace stay as written (Codex on #1260).
 */
const OPENING_MARKS = new RegExp(String.raw`^[\s${QUOTES_OPEN}${EMPHASIS}]*`, 'u');

/**
 * The first letter as a capital, for a sentence that now follows another:
 * «…кваліфікацію. Запитати, коли…», not «…кваліфікацію. запитати…» — the
 * colon that used to join them tolerated a lowercase start (#1110). Only an
 * ordinary word is touched; a case-sensitive token keeps its casing, since the
 * CRM report is posted for good (Codex on #1260). The capital does not depend
 * on the host's locale: a Turkish browser would make «improve» «İmprove» on
 * the panel while the CRM worker writes «Improve» (Codex on #1260).
 */
export function capitalizeFirst(text: string): string {
  const opening = OPENING_MARKS.exec(text)?.[0] ?? '';
  const word = text.slice(opening.length);
  if (!PLAIN_FIRST_WORD.test(word)) return text;
  return opening + word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * A lead closed as a sentence — «.», «!», «?» or «…», a closing quote or
 * emphasis after it included. «:» and «;» lead into what follows instead.
 */
const SENTENCE_CLOSED = new RegExp(`[${SENTENCE_CLOSE}]${CLOSING_RUN_NO_ASIDE}$`, 'u');

/**
 * A recommendation as every surface shows it — the panel, the PDF and the CRM
 * report alike: the lead ended once, and the advice that follows it as a
 * sentence of its own starting with a capital (#1110). After a lead ending in
 * «:» or «;» the advice continues the same sentence and keeps its casing
 * («Агент перебив; дати договорити», Codex on #1260), and so does an advice
 * with no lead before it. Idempotent, so a surface may apply it again to data
 * that did not come through the shared mapping (demo data).
 */
export function recommendationParts(
  lead: string,
  advice: string
): { lead: string; advice: string } {
  const ended = endSentence(lead);
  return {
    lead: ended,
    advice: SENTENCE_CLOSED.test(ended) ? capitalizeFirst(advice) : advice,
  };
}
